import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/authContext';
import { generatePersonalizedSummary } from '../services/api';
import '../styles/RecipeAnalysis.css';

const RecipeAnalysis = ({ recipe }) => {
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { user, token } = useAuth();

    useEffect(() => {
        const fetchSummary = async () => {
            if (!recipe || !user || !token) {
                setLoading(false);
                return;
            }
            
            try {
                setLoading(true);
                setError(null);
                const summaryData = await generatePersonalizedSummary(recipe.id, token);
                setSummary(summaryData);
            } catch (err) {
                console.error('Error fetching personalized summary:', err);
                setError('Unable to generate personalized summary');
            } finally {
                setLoading(false);
            }
        };

        fetchSummary();
    }, [recipe, user, token]);

    if (!user) {
        return null; // Don't show anything if user is not logged in
    }

    if (loading) {
        return (
            <div className="recipe-analysis">
                <div className="analysis-loading">
                    <div className="loading-spinner"></div>
                    <p>Generating personalized summary...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="recipe-analysis">
                <div className="analysis-error">
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    if (!summary) return null;

    return (
        <div className="recipe-analysis">
            <div className="analysis-header">
                <h3>Personalized Recipe Summary</h3>
            </div>

            <div className="analysis-content">
                {/* Personalized Summary */}
                <div className="analysis-section">
                    <h4>📝 Summary</h4>
                    <p>{summary.summary}</p>
                </div>

                {/* Allergy Warnings */}
                {summary.allergyWarnings && summary.allergyWarnings.length > 0 && (
                    <div className="analysis-section">
                        <h4>⚠️ Allergy Warnings</h4>
                        <div className="allergy-warnings">
                            {summary.allergyWarnings.map((warning, index) => (
                                <div key={index} className="allergy-warning">
                                    <strong>{warning.allergen}:</strong> {warning.description}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Dietary Considerations */}
                {summary.dietaryConsiderations && summary.dietaryConsiderations.length > 0 && (
                    <div className="analysis-section">
                        <h4>🥗 Dietary Considerations</h4>
                        <ul className="dietary-list">
                            {summary.dietaryConsiderations.map((consideration, index) => (
                                <li key={index}>{consideration}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Cooking Tips */}
                {summary.cookingTips && summary.cookingTips.length > 0 && (
                    <div className="analysis-section">
                        <h4>💡 Cooking Tips</h4>
                        <ul className="tips-list">
                            {summary.cookingTips.map((tip, index) => (
                                <li key={index}>{tip}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RecipeAnalysis; 